// @ts-nocheck
import { useQuery } from "@tanstack/react-query";
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import type {
  BillableActionsResponse,
  CreditTransactionHistoryResponse,
  ListCreditTransactionsParams,
  UserCredit,
} from "../../models";
import { customInstance } from "../../custom-instance";

type SecondParameter<T extends (...args: any) => any> = Parameters<T>[1];

/**
 * Get list of all billable actions and their credit costs. Public endpoint.
 */
export const listBillableActions = (
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<BillableActionsResponse>(
    { url: `/api/credits/actions/`, method: "GET", signal },
    options
  );
};

export const getListBillableActionsQueryKey = () => {
  return [`/api/credits/actions/`] as const;
};

export const getListBillableActionsQueryOptions = <
  TData = Awaited<ReturnType<typeof listBillableActions>>,
  TError = unknown
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<typeof listBillableActions>>,
      TError,
      TData
    >
  >;
  request?: SecondParameter<typeof customInstance>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListBillableActionsQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listBillableActions>>
  > = ({ signal }) => listBillableActions(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listBillableActions>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListBillableActionsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listBillableActions>>
>;
export type ListBillableActionsQueryError = unknown;

export function useListBillableActions<
  TData = Awaited<ReturnType<typeof listBillableActions>>,
  TError = unknown
>(options: {
  query: Partial<
    UseQueryOptions<
      Awaited<ReturnType<typeof listBillableActions>>,
      TError,
      TData
    >
  > &
    Pick<
      DefinedInitialDataOptions<
        Awaited<ReturnType<typeof listBillableActions>>,
        TError,
        Awaited<ReturnType<typeof listBillableActions>>
      >,
      "initialData"
    >;
  request?: SecondParameter<typeof customInstance>;
}): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListBillableActions<
  TData = Awaited<ReturnType<typeof listBillableActions>>,
  TError = unknown
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<typeof listBillableActions>>,
      TError,
      TData
    >
  > &
    Pick<
      UndefinedInitialDataOptions<
        Awaited<ReturnType<typeof listBillableActions>>,
        TError,
        Awaited<ReturnType<typeof listBillableActions>>
      >,
      "initialData"
    >;
  request?: SecondParameter<typeof customInstance>;
}): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListBillableActions<
  TData = Awaited<ReturnType<typeof listBillableActions>>,
  TError = unknown
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<typeof listBillableActions>>,
      TError,
      TData
    >
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useListBillableActions<
  TData = Awaited<ReturnType<typeof listBillableActions>>,
  TError = unknown
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<typeof listBillableActions>>,
      TError,
      TData
    >
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getListBillableActionsQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Get the authenticated user's credit balance, split by subscription and purchased credits.
 */
export const getCreditBalance = (
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<UserCredit>(
    { url: `/api/credits/balance/`, method: "GET", signal },
    options
  );
};

export const getGetCreditBalanceQueryKey = () => {
  return [`/api/credits/balance/`] as const;
};

export const getGetCreditBalanceQueryOptions = <
  TData = Awaited<ReturnType<typeof getCreditBalance>>,
  TError = unknown
>(options?: {
  query?: Partial<
    UseQueryOptions<Awaited<ReturnType<typeof getCreditBalance>>, TError, TData>
  >;
  request?: SecondParameter<typeof customInstance>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetCreditBalanceQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getCreditBalance>>
  > = ({ signal }) => getCreditBalance(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getCreditBalance>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetCreditBalanceQueryResult = NonNullable<
  Awaited<ReturnType<typeof getCreditBalance>>
>;
export type GetCreditBalanceQueryError = unknown;

export function useGetCreditBalance<
  TData = Awaited<ReturnType<typeof getCreditBalance>>,
  TError = unknown
>(options: {
  query: Partial<
    UseQueryOptions<Awaited<ReturnType<typeof getCreditBalance>>, TError, TData>
  > &
    Pick<
      DefinedInitialDataOptions<
        Awaited<ReturnType<typeof getCreditBalance>>,
        TError,
        Awaited<ReturnType<typeof getCreditBalance>>
      >,
      "initialData"
    >;
  request?: SecondParameter<typeof customInstance>;
}): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetCreditBalance<
  TData = Awaited<ReturnType<typeof getCreditBalance>>,
  TError = unknown
>(options?: {
  query?: Partial<
    UseQueryOptions<Awaited<ReturnType<typeof getCreditBalance>>, TError, TData>
  > &
    Pick<
      UndefinedInitialDataOptions<
        Awaited<ReturnType<typeof getCreditBalance>>,
        TError,
        Awaited<ReturnType<typeof getCreditBalance>>
      >,
      "initialData"
    >;
  request?: SecondParameter<typeof customInstance>;
}): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetCreditBalance<
  TData = Awaited<ReturnType<typeof getCreditBalance>>,
  TError = unknown
>(options?: {
  query?: Partial<
    UseQueryOptions<Awaited<ReturnType<typeof getCreditBalance>>, TError, TData>
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useGetCreditBalance<
  TData = Awaited<ReturnType<typeof getCreditBalance>>,
  TError = unknown
>(options?: {
  query?: Partial<
    UseQueryOptions<Awaited<ReturnType<typeof getCreditBalance>>, TError, TData>
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetCreditBalanceQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Get the authenticated user's credit transaction history.
 */
export const listCreditTransactions = (
  params?: ListCreditTransactionsParams,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<CreditTransactionHistoryResponse>(
    { url: `/api/credits/transactions/`, method: "GET", params, signal },
    options
  );
};

export const getListCreditTransactionsQueryKey = (
  params?: ListCreditTransactionsParams
) => {
  return [`/api/credits/transactions/`, ...(params ? [params] : [])] as const;
};

export const getListCreditTransactionsQueryOptions = <
  TData = Awaited<ReturnType<typeof listCreditTransactions>>,
  TError = unknown
>(
  params?: ListCreditTransactionsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listCreditTransactions>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customInstance>;
  }
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListCreditTransactionsQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listCreditTransactions>>
  > = ({ signal }) => listCreditTransactions(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listCreditTransactions>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListCreditTransactionsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listCreditTransactions>>
>;
export type ListCreditTransactionsQueryError = unknown;

export function useListCreditTransactions<
  TData = Awaited<ReturnType<typeof listCreditTransactions>>,
  TError = unknown
>(
  params: undefined | ListCreditTransactionsParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listCreditTransactions>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listCreditTransactions>>,
          TError,
          Awaited<ReturnType<typeof listCreditTransactions>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customInstance>;
  }
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListCreditTransactions<
  TData = Awaited<ReturnType<typeof listCreditTransactions>>,
  TError = unknown
>(
  params?: ListCreditTransactionsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listCreditTransactions>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listCreditTransactions>>,
          TError,
          Awaited<ReturnType<typeof listCreditTransactions>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customInstance>;
  }
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListCreditTransactions<
  TData = Awaited<ReturnType<typeof listCreditTransactions>>,
  TError = unknown
>(
  params?: ListCreditTransactionsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listCreditTransactions>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customInstance>;
  }
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useListCreditTransactions<
  TData = Awaited<ReturnType<typeof listCreditTransactions>>,
  TError = unknown
>(
  params?: ListCreditTransactionsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listCreditTransactions>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customInstance>;
  }
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getListCreditTransactionsQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}
